import { useFetchApisStatusForInformationAssetsPage } from "../../hooks/api-progress/information-asset-page";
import { useFetchInformationAssetAccesses } from "../../hooks/queries/information-asset-accesses";
import { useFetchAllSystemResources } from "../../hooks/queries/system-resources";
import { useFetchAllSystemUserGroups } from "../../hooks/queries/system-user-groups";
import { SystemUserGroup } from "../../models/system-user-group";
import { AppliedScope } from "./../../models/current-applied-scopes";
import { SystemResourceTableRecord } from "./model/view-object";

function useSystemResourceRecordGenerator(
  resourceId: String,
  selectedSystemName: String,
  selectedResourceType: String
): SystemResourceTableRecord[] {
  const systemResources = useFetchAllSystemResources();
  const informationAssetAccesses = useFetchInformationAssetAccesses();
  const systemUserGroups = useFetchAllSystemUserGroups();

  function querySystemUserGroup(groupId: String, systemId: String): SystemUserGroup | null {
    return (
      systemUserGroups.find(
        (systemUserGroup) =>
          systemUserGroup.systemUserGroupId === groupId && systemUserGroup.systemId === systemId
      ) ?? null
    );
  }

  function currentAppliedScopes(
    systemResourceId: String,
    systemId: String,
    assetId: String
  ): AppliedScope[] {
    return informationAssetAccesses.currentAppliedScopes(systemResourceId, systemId, assetId);
  }

  function individualUsersAdded(
    systemResourceId: String,
    systemId: String,
    assetId: String
  ): String[] {
    return informationAssetAccesses.individualUsersAdded(systemResourceId, systemId, assetId);
  }

  function groups(systemResourceId: String, systemId: String, assetId: String): String[] {
    const groupIds = informationAssetAccesses.groups(systemResourceId, systemId, assetId);
    const groupNames: String[] = [];
    groupIds.forEach((groupId: String) => {
      const groupName = querySystemUserGroup(groupId, systemId)?.name;
      if (groupName) {
        groupNames.push(groupName);
      }
    });
    return groupNames;
  }

  const associatedSystemResources = systemResources.fetchSystemResources(
    resourceId,
    selectedSystemName,
    selectedResourceType
  );

  return associatedSystemResources.map(
    (associatedSystemResource) =>
      new SystemResourceTableRecord(
        associatedSystemResource,
        individualUsersAdded(
          associatedSystemResource.systemResourceId,
          associatedSystemResource.system.id,
          resourceId
        ),
        groups(
          associatedSystemResource.systemResourceId,
          associatedSystemResource.system.id,
          resourceId
        ),
        resourceId,
        currentAppliedScopes(
          associatedSystemResource.systemResourceId,
          associatedSystemResource.system.id,
          resourceId
        )
      )
  );
}

export function useSystemResourcesTableController(props: {
  assetId: string;
  selectedSystem: string;
  selectedSystemResourceType: string;
  group: string | null;
}) {
  const informationAssetPageApiCallStatus = useFetchApisStatusForInformationAssetsPage();
  const assetId = props.assetId;
  const groupToBeFiltered = props.group;

  const systemResourceTableRecords = useSystemResourceRecordGenerator(
    assetId,
    props.selectedSystem ?? "",
    props.selectedSystemResourceType ?? ""
  );

  const getDynamicColumns = (): string[] => {
    if (systemResourceTableRecords === null) {
      return [];
    }
    const uniqueColumnNames = new Set<string>();
    systemResourceTableRecords.forEach((systemResourceTableRecord) =>
      Object.keys(systemResourceTableRecord.attributes).forEach((attribute) => {
        uniqueColumnNames.add(attribute);
      })
    );
    return Array.from(uniqueColumnNames);
  };

  const filteredGystemResourceTableRecords = groupToBeFiltered
    ? systemResourceTableRecords.filter((systemResourceTableRecord) =>
        systemResourceTableRecord.isAssociatedToGroup(groupToBeFiltered)
      )
    : systemResourceTableRecords;

  return {
    getSelectedResourceType: () => props.selectedSystemResourceType,
    getDynamicColumns,
    shouldRenderProgress: () => informationAssetPageApiCallStatus.isApiCallLoading,
    filteredGystemResourceTableRecords,
  };
}
